/**
 * Roster date helpers. Everything works on local calendar days as
 * 'YYYY-MM-DD' keys — never via toISOString(), which shifts the day for
 * anyone east of UTC before 05:30 IST.
 */

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** Matches MAX_RANGE_DAYS in the people service. */
export const MAX_RANGE_DAYS = 62;

export function toKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function fromKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(key: string, n: number): string {
  const d = fromKey(key);
  d.setDate(d.getDate() + n);
  return toKey(d);
}

/** Monday of the week containing `key`. */
export function startOfWeek(key: string): string {
  const d = fromKey(key);
  const offset = (d.getDay() + 6) % 7; // Mon = 0 … Sun = 6
  d.setDate(d.getDate() - offset);
  return toKey(d);
}

export const todayKey = () => toKey(new Date());

export function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** 0 = Mon … 6 = Sun, matching WEEKDAYS. */
export const weekdayIndex = (key: string) => (fromKey(key).getDay() + 6) % 7;

export function daysInRange(from: string, to: string): string[] {
  const out: string[] = [];
  for (let k = from; k <= to && out.length <= 400; k = addDays(k, 1)) out.push(k);
  return out;
}

export function formatRange(from: string, to: string) {
  const a = fromKey(from), b = fromKey(to);
  const month = (d: Date) => d.toLocaleDateString(undefined, { month: 'short' });
  if (a.getFullYear() !== b.getFullYear()) {
    return `${a.getDate()} ${month(a)} ${a.getFullYear()} – ${b.getDate()} ${month(b)} ${b.getFullYear()}`;
  }
  if (a.getMonth() !== b.getMonth()) return `${a.getDate()} ${month(a)} – ${b.getDate()} ${month(b)} ${b.getFullYear()}`;
  return `${a.getDate()}–${b.getDate()} ${month(b)} ${b.getFullYear()}`;
}

export const formatHours = (h: number) => `${Number.isInteger(h) ? h : h.toFixed(1)}h`;

/** Shift length in hours, wrapping past midnight ('22:00'→'06:00' = 8). */
export function shiftHours(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some(Number.isNaN)) return 0;
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  return Math.round((mins / 60) * 100) / 100;
}
